import { TableProps } from "@amzn/awsui-components-react/polaris/table";
import ButtonDropdown from "@amzn/awsui-components-react/polaris/button-dropdown";
import Popover from "@amzn/awsui-components-react/polaris/popover";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import StatusIndicator from "@amzn/awsui-components-react/polaris/status-indicator";
import { Link } from "react-router-dom";
import { formatDate, datePopOver } from "utils/date-format";
import { createLabelFunction } from "utils/table-utils";
import { getFileNameWithExtension } from "components/documents-table-os/utils";
import styles from "components/documents-table-os/style.module.scss";
import { AtlasColumnDefinitionsProps } from "./definitions";

const TICKET_ICON_BASE64 =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjUiIGhlaWdodD0iMjUiIHZpZXdCb3g9IjAgMCAyNSAyNSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxyZWN0IGZpbGw9IiM0NzUzNjMiIHdpZHRoPSIyNSIgaGVpZ2h0PSIyNSIgcng9IjIiLz48cGF0aCBkPSJNMTAuMTM3IDNoMy42NTF2NC4xNTJoNC4xOHYzLjI5aC00LjE4djcuODYyYy4wNTUuNzk0LjQ5NyAxLjE5MSAxLjMyNSAxLjE5MWgyLjg1NXYyLjgxMmMtMS4yMjIuNTMzLTIuNDcuNzU4LTMuNzQzLjY3Ny0xLjkxLS4xMjItNC45MDYtLjc5Ny00LjkwNi00LjMyNHYtOC4yMThIN1Y3LjY3NmwyLjQ2NS0uNTI0TDEwLjEzNyAzeiIgZmlsbD0iI0Y5MCIvPjwvZz48L3N2Zz4K";

const modifiedComparator = (field: string) => {
  return (a: any, b: any) => {
    const aVal = a[field] || "";
    const bVal = b[field] || "";
    if (aVal < bVal) return -1;
    if (aVal > bVal) return 1;
    return 0;
  };
};

export const disableDeleteIngestedDoc = (item: any): boolean => {
  return !(item.parsingStatus === "INDEXED" || item.parsingStatus === "FAILED");
};

export const getIngestionStatusIndicator = (status?: string) => {
  if (!status) return <span>Unknown</span>;
  const display = status.replace(/_/g, " ");
  switch (display) {
    case "IN QUEUE":
    case "STARTING":
    case "DELETE TRIGGERED":
      return <StatusIndicator type="pending">{display}</StatusIndicator>;
    case "IN PROGRESS":
      return <StatusIndicator type="in-progress">{display}</StatusIndicator>;
    case "FAILED":
      return <StatusIndicator type="error">{display}</StatusIndicator>;
    case "PARTIALLY INDEXED":
    case "NOT FOUND":
    case "TIMEOUT":
    case "IGNORED":
      return <StatusIndicator type="warning">{display}</StatusIndicator>;
    case "DELETING":
    case "DELETE IN PROGRESS":
      return <StatusIndicator type="info">{display}</StatusIndicator>;
    case "INDEXED":
      return <StatusIndicator type="success">{display}</StatusIndicator>;
    default:
      return <span>{display}</span>;
  }
};

export const createAtlasColumnDefinitions = (
  props: AtlasColumnDefinitionsProps,
): TableProps.ColumnDefinition<any>[] => [
  {
    id: "fileName",
    header: "Name",
    cell: (item) => (
      <Link
        to="#"
        className={styles.fileName}
        onClick={(e) => {
          e.preventDefault();
          props.onPreview?.(item);
        }}
      >
        {getFileNameWithExtension(item)}
      </Link>
    ),
    ariaLabel: createLabelFunction("fileName"),
    sortingField: "fileName",
    isRowHeader: true,
    width: props.showApprovalBatch ? "22%" : "25%",
  },
  {
    id: "size",
    header: "Size",
    cell: (item) => item.size || "Unknown",
    width: props.showApprovalBatch ? "10%" : "12%",
  },
  {
    id: "modifiedAt",
    header: "Uploaded time",
    ariaLabel: createLabelFunction("modifiedAt"),
    sortingField: "modifiedAt",
    sortingComparator: modifiedComparator("modifiedAt"),
    cell: (item) => (
      <div>
        <Popover
          dismissButton={false}
          content={datePopOver(Number(item.modifiedAt))}
        >
          {formatDate(Number(item.modifiedAt))}
        </Popover>
      </div>
    ),
    width: props.showApprovalBatch ? "13%" : "15%",
  },
  {
    id: "modifiedBy",
    header: "Uploaded by",
    cell: (item) => item.modifiedBy,
    width: props.showApprovalBatch ? "13%" : "15%",
  },
  {
    id: "parsingStatus",
    header: "Parsing status",
    cell: (item) => {
      const statusIndicator = getIngestionStatusIndicator(item.parsingStatus);

      return (
        <SpaceBetween direction="horizontal" size="xs">
          {statusIndicator}
          {item.ticketId && (
            <a
              href={`https://t.corp.amazon.com/${item.ticketId}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ticket ${item.ticketId}`}
            >
              <img
                src={TICKET_ICON_BASE64}
                alt="Ticket"
                style={{
                  width: 18,
                  height: 18,
                  cursor: "pointer",
                  verticalAlign: "top",
                }}
              />
            </a>
          )}
        </SpaceBetween>
      );
    },
    width: props.showApprovalBatch ? "13%" : "15%",
  },
  ...(props.showApprovalBatch
    ? [
        {
          id: "approvalBatch",
          header: "Approval Batch",
          cell: (item: any) =>
            item.reviewId ? (
              <Link to={`/reviews/kb-approvals/${item.reviewId}`}>View</Link>
            ) : (
              "Direct Upload"
            ),
          width: "12%",
        },
      ]
    : []),
  {
    id: "actions",
    header: "Actions",
    cell: (item) => (
      <div>
        <ButtonDropdown
          data-testid="document-button-dropdown"
          items={[
            {
              id: `dl.${item.documentId}`,
              text: "Download",
              disabled: !item.s3Key,
              disabledReason: "This is a link.",
            },
            {
              id: `rm.${item.documentId}`,
              text: "Delete",
              disabled: disableDeleteIngestedDoc(item),
              disabledReason: "Only INDEXED or FAILED documents may be deleted",
            },
          ]}
          variant="icon"
          onItemClick={props.handleItemActionClick}
          expandToViewport
        />
      </div>
    ),
    width: props.showApprovalBatch ? "17%" : "18%",
  },
];
